import { useEffect, useRef, useState, useMemo } from 'react'
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Line, Tooltip, Legend, BarChart, Bar, PieChart, Pie, Cell } from 'recharts'
import { getChartPalette, getModelColorMap } from '../../chartPalette'

const PALETTE = getChartPalette()
const FIXED_COLORS = getModelColorMap()

const MAX_BAR_SIZE = 90

const CustomLegend = ({ payload }) => {
  if (!payload || payload.length === 0) return null
  return (
    <div className="custom-legend-container">
      {payload.map((entry) => (
        <div className="legend-item" key={entry.value}>
          <span className="legend-color-dot" style={{ background: entry.color }} />
          <span className="legend-label">{entry.value}</span>
        </div>
      ))}
    </div>
  )
}

const add = (a, b) => (a || 0) + (Number(b) || 0)
const pad2 = (n) => (n < 10 ? `0${n}` : String(n))

function detectValueKey(row) {
  if (row == null) return null
  if ('totalRegistered' in row) return 'totalRegistered'
  if ('totalDownload' in row) return 'totalDownload'
  if ('total' in row) return 'total'
  return null
}

const transformChartDataGeneric = (apiData = []) => {
  const rawArray = Array.isArray(apiData) ? apiData : apiData?.data || []
  const valueKey = detectValueKey(rawArray[0]) || 'total'
  const raw = rawArray.filter((d) => d && d.year != null && d.month != null && d.model && d.model !== '')
  const quarterlyMap = {}
  raw.forEach((r) => {
    const y = Number(r.year)
    const m = Number(r.month)
    const q = Math.floor((m - 1) / 3) + 1
    const key = y * 4 + (q - 1)
    if (!quarterlyMap[key]) quarterlyMap[key] = {}
    quarterlyMap[key][r.model] = add(quarterlyMap[key][r.model], Number(r[valueKey] || 0))
  })

  const quarterly = Object.keys(quarterlyMap)
    .map(Number)
    .sort((a, b) => a - b)
    .map((k) => {
      const y = Math.floor(k / 4)
      const q = (k % 4) + 1
      return { date: `Q${q}/${y}`, ...quarterlyMap[k] }
    })

  const yearlyMap = {}
  raw.forEach((r) => {
    const y = Number(r.year)
    if (!yearlyMap[y]) yearlyMap[y] = {}
    yearlyMap[y][r.model] = add(yearlyMap[y][r.model], Number(r[valueKey] || 0))
  })

  const yearly = Object.keys(yearlyMap)
    .map(Number)
    .sort((a, b) => a - b)
    .map((y) => ({ date: String(y), ...yearlyMap[y] }))
  const monthlyMap = {}
  raw.forEach((r) => {
    const y = Number(r.year)
    const m = Number(r.month)
    const key = y * 12 + (m - 1)
    if (!monthlyMap[key]) monthlyMap[key] = {}
    monthlyMap[key][r.model] = add(monthlyMap[key][r.model], Number(r[valueKey] || 0))
  })
  const models = Array.from(new Set(raw.map((r) => r.model)))
  const monthly = Object.keys(monthlyMap)
    .map(Number)
    .sort((a, b) => a - b)
    .map((k) => {
      const y = Math.floor(k / 12)
      const m = (k % 12) + 1
      const mm = pad2(m)
      const base = { date: `${mm}/${y}` }
      models.forEach((mdl) => {
        base[mdl] = monthlyMap[k][mdl] || 0
      })
      return base
    })
  return { month: monthly, quarter: quarterly, year: yearly }
}

function buildFixedColorMap(modelKeys) {
  const map = {}
  let fallbackIndex = 0
  const usedFixedColors = Object.values(FIXED_COLORS)
  modelKeys.forEach((model) => {
    const key = String(model).trim()
    if (FIXED_COLORS[key]) {
      map[model] = FIXED_COLORS[key]
      return
    }
    while (usedFixedColors.includes(PALETTE[fallbackIndex % PALETTE.length])) {
      fallbackIndex += 1
    }
    map[model] = PALETTE[fallbackIndex % PALETTE.length]
    fallbackIndex += 1
  })
  return map
}

const SoftwareDownloadsChart = ({
  data = [],
  chartType = 'line',
  chartFrequency = 'year',
  g4ModelNames = [],
  hasSelectedModelFilter = false,
  selectedModelNames = [],
}) => {
  const containerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const transformed = useMemo(() => transformChartDataGeneric(data), [data])
  const chartData = useMemo(() => {
    if (chartFrequency === 'month') return transformed.month
    if (chartFrequency === 'quarter') return transformed.quarter
    if (chartFrequency === 'year') return transformed.year
    return transformed.year
  }, [transformed, chartFrequency])

  const reducedChartData = useMemo(() => {
    if (!chartData.length) return []
    const allModelKeys = Array.from(new Set(chartData.flatMap((row) => Object.keys(row).filter((k) => k !== 'date'))))
    if (hasSelectedModelFilter) {
      const selectedSet = new Set(Array.isArray(selectedModelNames) ? selectedModelNames : [])
      if (!selectedSet.size) {
        return chartData
      }

      return chartData.map((row) => {
        const nextRow = { date: row.date }
        selectedSet.forEach((model) => {
          nextRow[model] = Number(row[model] || 0)
        })
        return nextRow
      })
    }
    const g4Set = new Set(g4ModelNames)
    const visibleModels = allModelKeys.filter((model) => g4Set.has(model))
    const otherModels = allModelKeys.filter((model) => !g4Set.has(model))
    return chartData.map((row) => {
      const nextRow = { date: row.date }
      visibleModels.forEach((model) => {
        nextRow[model] = Number(row[model] || 0)
      })
      const othersTotal = otherModels.reduce((sum, model) => sum + Number(row[model] || 0), 0)
      if (othersTotal > 0) {
        nextRow.Others = othersTotal
      }
      return nextRow
    })
  }, [chartData, g4ModelNames, hasSelectedModelFilter, selectedModelNames])

  const modelKeys = useMemo(() => {
    const keys = Array.from(new Set(reducedChartData.flatMap((row) => Object.keys(row).filter((k) => k !== 'date'))))
    return keys.sort((a, b) => {
      if (a === 'Others') return 1
      if (b === 'Others') return -1
      return a.localeCompare(b)
    })
  }, [reducedChartData])

  const colorMap = useMemo(() => buildFixedColorMap(modelKeys), [modelKeys])

  const pieData = useMemo(() => {
    return modelKeys
      .map((model) => ({
        name: model,
        value: reducedChartData.reduce((sum, row) => sum + Number(row[model] || 0), 0),
      }))
      .filter((item) => item.value > 0)
  }, [modelKeys, reducedChartData])

  const renderChart = () => {
    if (!reducedChartData.length) {
      return null
    }
    switch (chartType) {
      case 'bar':
        return (
          <BarChart data={reducedChartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend content={<CustomLegend />} />
            {modelKeys.map((model) => (
              <Bar maxBarSize={MAX_BAR_SIZE} key={model} dataKey={model} fill={colorMap[model]} name={model} isAnimationActive={false} />
            ))}
          </BarChart>
        )
      case 'pie':
        return (
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="55%" outerRadius={235} isAnimationActive={false}>
              {pieData.map((entry) => (
                <Cell key={entry.name} fill={colorMap[entry.name]} />
              ))}
            </Pie>
            <Legend content={<CustomLegend />} />
            <Tooltip />
          </PieChart>
        )
      case 'stackedBar':
        return (
          <BarChart data={reducedChartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend content={<CustomLegend />} />
            {modelKeys.map((model) => (
              <Bar key={model} dataKey={model} maxBarSize={MAX_BAR_SIZE} fill={colorMap[model]} name={model} stackId="total" isAnimationActive={false} />
            ))}
          </BarChart>
        )
      case 'line':
      default:
        return (
          <LineChart data={reducedChartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend content={<CustomLegend />} />
            {modelKeys.map((model) => (
              <Line key={model} type="monotone" dataKey={model} stroke={colorMap[model]} strokeWidth={2} dot={false} name={model} isAnimationActive={false} />
            ))}
          </LineChart>
        )
    }
  }

  useEffect(() => {
    const observer = new window.ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect
        setDimensions({ width, height })
      }
    })
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} style={{ width: '100%', height: 650 }} className="chart-responsive-wrapper">
      <div className="chart-min-width">
        {dimensions.width > 0 && dimensions.height > 0 && (
          <ResponsiveContainer width="100%" height="100%" key={`${dimensions.width}x${dimensions.height}`}>
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

export default SoftwareDownloadsChart

